import React, { useMemo, useCallback, useState, Fragment } from 'react';
import { Line, Player } from './DialogPlayBuilder';
import { Typography, Box, IconButton, Tooltip } from '@mui/material';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTranslation } from 'react-i18next';
import Snackbar from '@mui/material/Snackbar';
import FeedbackIcon from '@mui/icons-material/Feedback';

interface Props {
    lines: Line[];
    players: Player[];
    onUpdateLines: (lines: Line[]) => void;
    onLineHover?: (lineId: number | null) => void;
}

export default function PlayAnimationLinesOrder({ lines, players, onUpdateLines, onLineHover }: Props) {
    const { t } = useTranslation();
    const [snackbar, setSnackbar] = useState({ open: false, message: '', color: '#00398F' });

    const showSnackbar = (message: string) => {
        setSnackbar({ open: true, message, color: '#00398F' });
        setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
    };

    const toggleConcurrentGroup = useCallback(
        (line: Line) => {
            const sortedLines = [...lines].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            const currentIndex = sortedLines.findIndex((l) => l.id === line.id);

            if (line.sameMomentAsPrevious) {
                onUpdateLines(lines.map((l) => (l.id === line.id ? { ...l, sameMomentAsPrevious: undefined } : l)));
                return;
            }

            if (currentIndex > 0) {
                const prevLine = sortedLines[currentIndex - 1];
                if (prevLine.fromId === line.fromId) {
                    showSnackbar(t('PlayAnimationLinesOrder06', { playerId: line.fromId }));
                    return;
                }

                let groupStartIndex = currentIndex - 1;
                while (groupStartIndex > 0 && sortedLines[groupStartIndex].sameMomentAsPrevious) {
                    groupStartIndex--;
                }
                const concurrentGroup = sortedLines.slice(groupStartIndex, currentIndex);
                if (concurrentGroup.some((l) => l.fromId === line.fromId)) {
                    showSnackbar(t('PlayAnimationLinesOrder06', { playerId: line.fromId }));
                    return;
                }
            }

            onUpdateLines(lines.map((l) => (l.id === line.id ? { ...l, sameMomentAsPrevious: true } : l)));
        },
        [lines, onUpdateLines, t]
    );

    const movementsText: Record<string, string> = {
        dashed: t('ActionButtons03'),
        straight: t('ActionButtons01'),
        zigzag: t('ActionButtons02'),
        bar: t('ActionButtons04'),
        shot: t('ActionButtons11'),
        handoff: t('ActionButtons16'),
    };

    const groupColors = ['#fff3e0', '#fffde7'];

    const concurrentLineColors = useMemo(() => {
        const sorted = [...lines].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const colorMap = new Map<number | string, string>();
        let currentColor: string | null = null;
        let colorIndex = 0;

        sorted.forEach((line, index) => {
            if (line.sameMomentAsPrevious && index > 0) {
                if (!currentColor) {
                    currentColor = groupColors[colorIndex % groupColors.length];
                    colorIndex++;
                }
                colorMap.set(sorted[index - 1].id, currentColor);
                colorMap.set(line.id, currentColor);
            } else {
                currentColor = null;
            }
        });

        return colorMap;
    }, [lines]);

    const moveLine = useCallback(
        (index: number, direction: 'up' | 'down') => {
            const sorted = [...lines].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            const targetIndex = direction === 'up' ? index - 1 : index + 1;

            if (targetIndex < 0 || targetIndex >= sorted.length) return;

            const reordered = [...sorted];
            const [moved] = reordered.splice(index, 1);
            reordered.splice(targetIndex, 0, moved);

            const updated = reordered.map((line, idx) => ({ ...line, order: idx + 1 }));

            for (let i = 1; i < updated.length; i++) {
                if (updated[i].sameMomentAsPrevious && updated[i].fromId === updated[i - 1].fromId) {
                    updated[i] = { ...updated[i], sameMomentAsPrevious: undefined };
                }
            }

            if (direction === 'up' && targetIndex > 0 && updated[targetIndex].sameMomentAsPrevious && updated[targetIndex].fromId === updated[targetIndex - 1].fromId) {
                updated[targetIndex] = { ...updated[targetIndex], sameMomentAsPrevious: undefined };
            } else if (direction === 'down' && targetIndex < updated.length - 1 && updated[targetIndex + 1].sameMomentAsPrevious && updated[targetIndex + 1].fromId === updated[targetIndex].fromId) {
                updated[targetIndex + 1] = { ...updated[targetIndex + 1], sameMomentAsPrevious: undefined };
            }

            onUpdateLines(updated);
        },
        [lines, onUpdateLines]
    );

    const sortedLines = [...lines].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return (
        <Fragment>
            {/* Title */}
            <Typography sx={{
                fontSize: '12px',
                textTransform: 'uppercase',
                textAlign: 'center',
                fontWeight: '600',
                color: 'green',
                mb: 2,
                mt: 2,
            }}>
                {t('PlayAnimationLinesOrder11')}
            </Typography>

            <Box sx={{ bgcolor: 'transparent' }}>
                {sortedLines.map((line, index) => {
                    const player = players.find((p) => p.id === line.fromId);
                    const displayNumber = player
                        ? player.isCoach
                            ? 'C'
                            : player.isSpecialDefender
                                ? String(player.number)
                                : player.isDefender
                                    ? `X${player.number}`
                                    : String(player.number)
                        : (line.fromId <= 0 ? '' : line.fromId);
                    const isStartOfGroup = index === 0 || !sortedLines[index].sameMomentAsPrevious;
                    const nextIsConcurrent = index < sortedLines.length - 1 && sortedLines[index + 1].sameMomentAsPrevious;
                    const mb = (index < sortedLines.length - 1 && !sortedLines[index + 1].sameMomentAsPrevious) ? 0.5 : 0.15;

                    return (
                        <Fragment key={line.id}>
                            {isStartOfGroup && nextIsConcurrent && (
                                <Typography sx={{
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    color: '#f57c00',
                                    textAlign: 'center',
                                    mb: 0.25,
                                    mt: index > 0 ? 0.25 : 0,
                                }}>
                                    {t('PlayAnimationLinesOrder10')}
                                </Typography>
                            )}
                            <Box
                                onMouseEnter={() => onLineHover?.(line.id)}
                                onMouseLeave={() => onLineHover?.(null)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    bgcolor: concurrentLineColors.get(line.id) ?? 'white',
                                    borderRadius: '6px',
                                    border: '1px solid #e0e0e0',
                                    mb,
                                    py: 0.5,
                                    px: 0.75,
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: concurrentLineColors.get(line.id) ?? '#f5f5f5',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                                        transform: 'translateY(-1px)',
                                        borderColor: '#9333ea',
                                    },
                                }}
                            >
                                <Tooltip title={t('PlayAnimationLinesOrder05')} enterDelay={1000}>
                                    <IconButton
                                        onClick={() => toggleConcurrentGroup(line)}
                                        size="small"
                                        sx={{
                                            color: line.sameMomentAsPrevious ? '#f57c00' : '#9e9e9e',
                                            p: 0.25,
                                            minWidth: 0,
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                                        }}
                                    >
                                        <RestoreRoundedIcon sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                </Tooltip>

                                <Typography sx={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#333',
                                    // flexGrow: 1,
                                    ml: 0.5
                                }}>
                                    {movementsText[line.type] || line.type.toUpperCase()} {t('PlayAnimationLinesOrder12')} {displayNumber}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 0.25 }}>
                                    {index > 0 && (
                                        <Tooltip title={t('CRUDList01')} enterDelay={1000}>
                                            <IconButton
                                                onClick={() => moveLine(index, 'up')}
                                                size="small"
                                                sx={{
                                                    color: '#00398f',
                                                    p: 0.25,
                                                    minWidth: 0,
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0,57,143,0.08)',
                                                        color: '#002d6b'
                                                    }
                                                }}
                                            >
                                                <ArrowUpwardIcon sx={{ fontSize: '16px' }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {index < sortedLines.length - 1 && (
                                        <Tooltip title={t('CRUDList02')} enterDelay={1000}>
                                            <IconButton
                                                onClick={() => moveLine(index, 'down')}
                                                size="small"
                                                sx={{
                                                    color: '#00398f',
                                                    p: 0.25,
                                                    minWidth: 0,
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0,57,143,0.08)',
                                                        color: '#002d6b'
                                                    }
                                                }}
                                            >
                                                <ArrowDownwardIcon sx={{ fontSize: '16px' }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        </Fragment>
                    );
                })}
            </Box>

            <Snackbar
                open={snackbar.open}
                message={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FeedbackIcon sx={{ mr: 1, fontSize: '18px' }} />
                        {snackbar.message}
                    </Box>
                }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ '& .MuiSnackbarContent-root': { bgcolor: snackbar.color, color: '#fff', borderRadius: '6px' } }}
            />
        </Fragment>
    );
}